import React from 'react';
import { Head, Link } from '@inertiajs/react';
import AdminLayout from '../../../components/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { ArrowLeft, Edit, Trash2 } from 'lucide-react';

interface User {
  id: number;
  email_address: string;
  role: string;
  created_at: string;
  updated_at: string;
}

interface Props {
  user: User;
}

const ShowUser: React.FC<Props> = ({ user }) => {
  return (
    <AdminLayout title={`Utilisateur #${user.id}`}>
      <Head title={`Utilisateur ${user.email_address} - Admin`} />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Utilisateur #{user.id}</h1>
              <p className="text-gray-600">{user.email_address}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Link href={`/admin/users/${user.id}/edit`}>
              <Button variant="outline">
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            </Link>
            <Link 
              href={`/admin/users/${user.id}`} 
              method="delete" 
              as="button"
              onBefore={() => {
                if (!confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
                  return false;
                }
              }}
            >
              <Button variant="outline" className="text-red-600 hover:text-red-800">
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            </Link>
          </div>
        </div>

        {/* User Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Informations générales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">ID</label>
                <p className="text-lg font-semibold text-gray-900">#{user.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Adresse email</label>
                <p className="text-lg font-semibold text-gray-900">{user.email_address}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Rôle</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  user.role === 'admin' 
                    ? 'bg-red-100 text-red-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {user.role === 'admin' ? 'Administrateur' : 'Utilisateur'}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Dates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Créé le</label>
                <p className="text-lg font-semibold text-gray-900">
                  {new Date(user.created_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Modifié le</label>
                <p className="text-lg font-semibold text-gray-900">
                  {new Date(user.updated_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              <Link href="/admin/users">
                <Button variant="outline">
                  Voir tous les utilisateurs
                </Button>
              </Link>
              <Link href="/admin/users/new">
                <Button>
                  Créer un nouvel utilisateur
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ShowUser; 
